import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { Crown, Check, RotateCcw, Receipt, Mail, Loader2, AlertTriangle, RefreshCw } from 'lucide-react'
import { subscriptionApi } from '../api/services.js'
import toast from 'react-hot-toast'

// ─── Same colors & features as BrainCamp RN app ──────────────────────────────
const PLAN_COLORS = {
  trial:      '#64748b',
  starter:    '#2563eb',
  growth:     '#7c3aed',
  pro:        '#0891b2',
  enterprise: '#16a34a',
}

const PLAN_FEATURES = {
  trial:      ['50 students', '1 month free', 'All basic features', 'Email support'],
  starter:    ['200 students', 'Attendance & Homework', 'Fee management', 'Email + Chat support'],
  growth:     ['500 students', 'Everything in Starter', 'Advanced reports', 'Priority support'],
  pro:        ['1000 students', 'Everything in Growth', 'Custom branding', 'Dedicated support'],
  enterprise: ['Unlimited students', 'Everything in Pro', 'API access', '24/7 phone support'],
}

// ─── Load Razorpay script once ────────────────────────────────────────────────
function loadRazorpay() {
  return new Promise((resolve) => {
    if (window.Razorpay) { resolve(true); return }
    const script = document.createElement('script')
    script.src = 'https://checkout.razorpay.com/v1/checkout.js'
    script.onload = () => resolve(true)
    script.onerror = () => resolve(false)
    document.body.appendChild(script)
  })
}

export default function SubscriptionPage() {
  const { user } = useSelector((s) => s.auth)
  const [data, setData]       = useState(null)   // { subscription, studentCount, plans, schoolEmail }
  const [loading, setLoading] = useState(true)
  const [paying, setPaying]   = useState(false)
  const [cancelling, setCancelling] = useState(false)

  const fetchData = async () => {
    try {
      setLoading(true)
      const res = await subscriptionApi.get()
      // Backend returns { subscription, studentCount, plans, schoolEmail }
      setData(res?.subscription ? res : res?.data ?? res)
    } catch {
      toast.error('Failed to load subscription')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { fetchData() }, [])

  const sub          = data?.subscription
  const plans        = data?.plans || []
  const studentCount = data?.studentCount || 0
  const schoolEmail  = data?.schoolEmail || user?.email || ''

  const daysLeft = sub
    ? Math.max(0, Math.ceil((new Date(sub.endDate).getTime() - Date.now()) / 86400000))
    : 0
  const isExpired = sub && new Date(sub.endDate) < new Date()
  const usagePct  = sub?.studentLimit === -1
    ? 0
    : Math.min(100, (studentCount / (sub?.studentLimit || 1)) * 100)

  const planColor = PLAN_COLORS[sub?.plan] || '#2563eb'

  // ─── Payments ──────────────────────────────────────────────────────────────
  const handleSelectPlan = async (plan, mode) => {
    if (plan.price === 0) {
      try {
        await subscriptionApi.startTrial()
        toast.success('30-day free trial activated!')
        fetchData()
      } catch (err) {
        toast.error(err.response?.data?.message || 'Failed to start trial')
      }
      return
    }

    const loaded = await loadRazorpay()
    if (!loaded) { toast.error('Failed to load payment gateway. Check your connection.'); return }

    setPaying(true)
    try {
      if (mode === 'auto') {
        // Recurring / Auto-Pay
        const res = await subscriptionApi.createRecurring({ planId: plan.id })
        const { subscriptionId, keyId } = res
        const rzp = new window.Razorpay({
          key:             keyId,
          subscription_id: subscriptionId,
          name:            'BrainCamp',
          description:     `BrainCamp ${plan.name} — Auto-Pay`,
          prefill: { name: user?.name || '', email: user?.email || schoolEmail, contact: user?.mobile || '' },
          theme: { color: PLAN_COLORS[plan.id] || '#2563eb' },
          handler: async (response) => {
            try {
              await subscriptionApi.verifyRecurring({
                razorpay_payment_id:      response.razorpay_payment_id,
                razorpay_subscription_id: response.razorpay_subscription_id,
                razorpay_signature:       response.razorpay_signature,
                planId: plan.id,
              })
              toast.success(`Auto-Pay activated! ₹${plan.price.toLocaleString('en-IN')}/month`)
              fetchData()
            } catch (err) {
              toast.error(err.response?.data?.message || 'Verification failed. Contact support.')
            }
          },
        })
        rzp.on('payment.failed', (e) => {
          if (e.error?.description !== 'Cancelled by user') {
            toast.error(e.error?.description || 'Payment failed')
          }
        })
        rzp.open()
      } else {
        // Pay Once
        const res = await subscriptionApi.createOrder({ planId: plan.id })
        const { orderId, amount, keyId } = res
        const rzp = new window.Razorpay({
          key:      keyId,
          amount,
          currency: 'INR',
          order_id: orderId,
          name:     'BrainCamp',
          description: `BrainCamp ${plan.name} Subscription`,
          prefill: { name: user?.name || '', email: user?.email || schoolEmail, contact: user?.mobile || '' },
          theme: { color: PLAN_COLORS[plan.id] || '#2563eb' },
          handler: async (response) => {
            try {
              await subscriptionApi.verifyPayment({
                razorpay_order_id:   response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature:  response.razorpay_signature,
                planId: plan.id,
              })
              toast.success(`Subscribed to ${plan.name} plan!`)
              fetchData()
            } catch (err) {
              toast.error(err.response?.data?.message || 'Verification failed. Contact support.')
            }
          },
        })
        rzp.on('payment.failed', (e) => {
          if (e.error?.description !== 'Cancelled by user') {
            toast.error(e.error?.description || 'Payment failed')
          }
        })
        rzp.open()
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not initiate payment. Try again.')
    } finally {
      setPaying(false)
    }
  }

  const handleCancelAutoRenew = async () => {
    if (!window.confirm('Cancel Auto-Pay? Your plan stays active until end of billing period.')) return
    try {
      setCancelling(true)
      await subscriptionApi.cancelAutoRenew()
      toast.success('Auto-Pay cancelled.')
      fetchData()
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to cancel auto-pay')
    } finally {
      setCancelling(false)
    }
  }

  if (loading) {
    return (
      <div className="space-y-4">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="card p-5 animate-pulse h-24 bg-gray-50" />
        ))}
      </div>
    )
  }

  return (
    <div className="space-y-6 max-w-3xl">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Subscription</h2>
          <p className="text-sm text-gray-500 mt-0.5">Manage your BrainCamp plan and billing</p>
        </div>
        <button onClick={fetchData} className="btn-secondary p-2"><RefreshCw className="w-4 h-4" /></button>
      </div>

      {/* ── Current Plan Status ── */}
      {sub && (
        <div
          className="card p-5 border-2"
          style={{ borderColor: isExpired ? '#fca5a5' : `${planColor}40`, backgroundColor: isExpired ? '#fef2f2' : `${planColor}0d` }}
        >
          <div className="flex items-start justify-between gap-4 mb-4">
            <div>
              <p className="text-xs text-gray-500 mb-0.5">Current Plan</p>
              <p className="text-2xl font-extrabold" style={{ color: isExpired ? '#dc2626' : planColor }}>
                {plans.find((p) => p.id === sub.plan)?.name || sub.plan}
              </p>
            </div>
            <span
              className="text-xs font-bold px-3 py-1.5 rounded-xl text-white flex-shrink-0"
              style={{ backgroundColor: isExpired ? '#dc2626' : planColor }}
            >
              {isExpired ? 'EXPIRED' : `${daysLeft}d left`}
            </span>
          </div>

          {/* Auto-renew row */}
          <div className={`flex items-center gap-2 text-xs font-medium px-3 py-2 rounded-lg border mb-4 ${sub.autoRenew ? 'bg-green-50 border-green-200 text-green-700' : 'bg-gray-50 border-gray-200 text-gray-500'}`}>
            <RotateCcw className="w-3.5 h-3.5 flex-shrink-0" />
            <span className="flex-1">{sub.autoRenew ? 'Auto-Pay ON — renews monthly' : 'Auto-Pay OFF'}</span>
            {sub.autoRenew && (
              <button
                onClick={handleCancelAutoRenew}
                disabled={cancelling}
                className="text-xs text-red-600 bg-red-50 hover:bg-red-100 px-2 py-0.5 rounded-md font-semibold transition-colors"
              >
                {cancelling ? '...' : 'Cancel'}
              </button>
            )}
          </div>

          {/* Student usage */}
          {sub.studentLimit !== -1 && (
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-xs text-gray-500">Students: {studentCount} / {sub.studentLimit}</span>
                <span className={`text-xs font-bold ${usagePct > 80 ? 'text-red-600' : 'text-gray-500'}`}>{usagePct.toFixed(0)}%</span>
              </div>
              <div className="h-2.5 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full transition-all duration-500"
                  style={{ width: `${usagePct}%`, backgroundColor: usagePct > 80 ? '#dc2626' : planColor }}
                />
              </div>
              {usagePct > 80 && (
                <p className="text-xs text-red-600 mt-1.5 flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" /> Approaching student limit. Consider upgrading.
                </p>
              )}
            </div>
          )}

          {isExpired && (
            <p className="text-sm text-red-600 font-medium mt-3">
              Your subscription has expired. Renew below to continue using BrainCamp.
            </p>
          )}
        </div>
      )}

      {/* ── Choose a Plan ── */}
      <div>
        <h3 className="font-bold text-gray-900 mb-1">Choose a Plan</h3>
        <p className="text-xs text-gray-500 mb-4">All plans include attendance, homework, exams, results, messaging and fee management</p>

        <div className="space-y-4">
          {plans.map((plan) => {
            const isCurrentPlan = sub?.plan === plan.id
            const color         = PLAN_COLORS[plan.id] || '#2563eb'
            const features      = PLAN_FEATURES[plan.id] || []

            return (
              <div
                key={plan.id}
                className="card p-5 relative"
                style={{ borderWidth: 2, borderColor: isCurrentPlan ? color : '#e5e7eb' }}
              >
                {plan.popular && (
                  <div
                    className="absolute -top-3 right-4 text-[10px] font-bold px-3 py-1 rounded-full text-white"
                    style={{ backgroundColor: color }}
                  >
                    ⭐ POPULAR
                  </div>
                )}
                {isCurrentPlan && (
                  <div
                    className="absolute top-3 right-3 text-[10px] font-bold px-2.5 py-1 rounded-lg text-white"
                    style={{ backgroundColor: color }}
                  >
                    ✓ CURRENT
                  </div>
                )}

                <div className="flex items-start justify-between gap-4 mb-3">
                  <div>
                    <p className="font-bold text-gray-900 text-lg">{plan.name}</p>
                    <p className="font-extrabold text-2xl mt-0.5" style={{ color }}>
                      {plan.price === 0 ? 'Free' : `₹${plan.price.toLocaleString('en-IN')}/month`}
                    </p>
                    <p className="text-xs text-gray-400 mt-0.5">
                      {plan.studentLimit === -1 ? 'Unlimited' : `Up to ${plan.studentLimit}`} students
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-x-4 gap-y-1 mb-4">
                  {features.map((f) => (
                    <div key={f} className="flex items-center gap-1.5">
                      <Check className="w-3.5 h-3.5 flex-shrink-0" style={{ color }} />
                      <span className="text-xs text-gray-600">{f}</span>
                    </div>
                  ))}
                </div>

                {!isCurrentPlan && plan.price === 0 && (
                  <button
                    onClick={() => handleSelectPlan(plan, 'once')}
                    disabled={paying || sub?.trialUsed}
                    className="w-full py-2.5 rounded-xl text-sm font-semibold text-white transition-opacity disabled:opacity-50"
                    style={{ backgroundColor: color }}
                  >
                    {sub?.trialUsed ? 'Trial Already Used' : 'Start Free Trial'}
                  </button>
                )}

                {!isCurrentPlan && plan.price > 0 && (
                  <div className="flex gap-3">
                    <button
                      onClick={() => handleSelectPlan(plan, 'auto')}
                      disabled={paying}
                      className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-white flex items-center justify-center gap-1.5 transition-opacity disabled:opacity-50"
                      style={{ backgroundColor: color }}
                    >
                      {paying ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />}
                      Auto-Pay ₹{plan.price.toLocaleString('en-IN')}/mo
                    </button>
                    <button
                      onClick={() => handleSelectPlan(plan, 'once')}
                      disabled={paying}
                      className="flex-1 py-2.5 rounded-xl text-sm font-semibold transition-colors disabled:opacity-50"
                      style={{ border: `1.5px solid ${color}`, color }}
                    >
                      Pay Once
                    </button>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </div>

      {/* ── Payment History ── */}
      {sub?.payments?.length > 0 && (
        <div className="card p-5">
          <h3 className="font-bold text-gray-900 mb-4">Payment History</h3>
          <div className="space-y-2">
            {[...sub.payments].reverse().slice(0, 5).map((p, i) => (
              <div key={i} className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                <Receipt className="w-4 h-4 text-gray-400 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900">
                    {plans.find((pl) => pl.id === p.plan)?.name || p.plan} Plan
                  </p>
                  <p className="text-xs text-gray-400">
                    {p.date ? new Date(p.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : ''}
                  </p>
                </div>
                <span className="text-sm font-bold text-green-600">₹{p.amount?.toLocaleString('en-IN')}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Contact Support ── */}
      <a
        href="mailto:support@braincamp.co.in"
        className="card p-4 flex items-center justify-center gap-2 text-sm font-medium text-primary-600 hover:bg-blue-50 transition-colors"
      >
        <Mail className="w-4 h-4" />
        Contact support@braincamp.co.in
      </a>
    </div>
  )
}
